/**
 * MongoDB implementation of AuthSession Repository
 * 
 * Infrastructure layer - no domain logic
 */
import { MongoClient, Db, Collection } from 'mongodb';
import { AuthSession } from '../../bounded-contexts/authentication/domain/aggregates/AuthSession';
import { IAuthSessionRepository } from '../../bounded-contexts/authentication/domain/repositories/IAuthSessionRepository';

export class MongoAuthSessionRepository implements IAuthSessionRepository {
  private db: Db;
  private collection: Collection;
  private client: MongoClient;

  constructor(
    private readonly mongoUrl: string,
    private readonly dbName: string
  ) {}

  private async ensureConnection(): Promise<void> {
    if (!this.client) {
      this.client = new MongoClient(this.mongoUrl);
      await this.client.connect();
      this.db = this.client.db(this.dbName);
      this.collection = this.db.collection('auth_sessions');
      
      // Create indexes
      await this.collection.createIndex({ personId: 1 });
      await this.collection.createIndex({ expiresAt: 1 });
      await this.collection.createIndex({ isActive: 1 });
    }
  }

  async save(session: AuthSession): Promise<void> {
    await this.ensureConnection();
    const snapshot = session.toSnapshot();
    
    await this.collection.replaceOne(
      { _id: snapshot.id },
      { _id: snapshot.id, ...snapshot },
      { upsert: true }
    );
  }

  async findById(sessionId: string): Promise<AuthSession | null> {
    await this.ensureConnection();
    const doc = await this.collection.findOne({ _id: sessionId });
    
    if (!doc) return null;
    
    return AuthSession.fromSnapshot(doc);
  }

  async findByPersonId(personId: string): Promise<AuthSession[]> {
    await this.ensureConnection();
    const docs = await this.collection.find({ personId }).toArray();
    
    return docs.map(doc => AuthSession.fromSnapshot(doc));
  }

  async findActiveByPersonId(personId: string): Promise<AuthSession[]> {
    await this.ensureConnection();
    const now = new Date();
    
    const docs = await this.collection.find({
      personId,
      isActive: true,
      expiresAt: { $gt: now }
    }).toArray();
    
    return docs.map(doc => AuthSession.fromSnapshot(doc));
  }

  async remove(sessionId: string): Promise<void> {
    await this.ensureConnection();
    await this.collection.deleteOne({ _id: sessionId });
  }

  async removeByPersonId(personId: string): Promise<void> {
    await this.ensureConnection();
    await this.collection.deleteMany({ personId });
  }

  async removeExpired(): Promise<number> {
    await this.ensureConnection();
    const now = new Date();
    
    const result = await this.collection.deleteMany({
      expiresAt: { $lt: now }
    });
    
    return result.deletedCount;
  }

  async disconnect(): Promise<void> {
    if (this.client) {
      await this.client.close();
    }
  }
}